import { FC } from 'react';
import { MdDeleteForever } from 'react-icons/md';
import { ExtendableBlock } from '../../../components/extendable-block/extendable-block';
import { IconButton } from '../../../components/icon-button/icon-button';
import { InlineInputs } from '../../../components/inline-inputs/inline-inputs';
import { formatToBackDateTime } from '../../../helpers/date-time';
import { useLang } from '../../../hooks/use-lang';
import { DatePicker } from '../../date-time/date-picker/date-picker';
import { TimePicker } from '../../date-time/time-picker/time-picker';
import { WorkingHoursType } from '../types';
import s from './working-hours-specs.module.css';

interface Props {
  specs?: WorkingHoursType['specs'];
  handleUpdateSpecs: (specs: WorkingHoursType['specs']) => void;
}

export const WorkingHoursSpecs: FC<Props> = ({ handleUpdateSpecs, specs }) => {
  const { t } = useLang();

  const updatedSpecs = [...(specs || [{}])];

  const handleSelectDate = (date?: Date, index: number = 0) => {
    const stringifiedDate = formatToBackDateTime(date);
    updatedSpecs[index] = { ...updatedSpecs[index], date: stringifiedDate };
    handleUpdateSpecs(updatedSpecs);
  };

  const handleSelectFrom = (from: string, index: number) => {
    let updatedTime = updatedSpecs[index].time || '00:00-00:00';
    const [, to] = updatedTime.split('-');
    updatedTime = `${from}-${to}`;
    updatedSpecs[index].time = updatedTime;
    handleUpdateSpecs(updatedSpecs);
  };

  const handleSelectTo = (to: string, index: number) => {
    let updatedTime = updatedSpecs[index].time || '00:00-00:00';
    const [from] = updatedTime.split('-');
    updatedTime = `${from}-${to}`;
    updatedSpecs[index].time = updatedTime;
    handleUpdateSpecs(updatedSpecs);
  };

  const handleAddSpec = () => {
    updatedSpecs.push({});
    handleUpdateSpecs(updatedSpecs);
  };

  const handleDeleteSpec = (index: number) => {
    updatedSpecs.splice(index, 1);
    handleUpdateSpecs(updatedSpecs);
  };

  return (
    <ExtendableBlock
      button={{
        variant: 'primary',
        children: t('Добавить'),
        onClick: handleAddSpec,
        id: 'add-spec-button',
      }}
      className={s.feature}
    >
      {updatedSpecs.map(({ date, time }, index) => (
        <li key={index} className={s.item}>
          <DatePicker
            selectedDate={date ? new Date(date) : undefined}
            onSelectDate={(date) => handleSelectDate(date, index)}
            required
            name={`spec-date-${index}`}
            placeholder={t('Дата')}
            data-testid={`spec-date-${index}`}
          />
          <InlineInputs>
            <TimePicker
              value={time?.split('-')[0]}
              onChange={(e) => handleSelectFrom(e, index)}
              required
              name={`spec-from-${index}`}
              placeholder={t('От')}
              data-testid={`spec-from-${index}`}
            />
            <TimePicker
              value={time?.split('-')[1]}
              onChange={(e) => handleSelectTo(e, index)}
              required
              name={`spec-to-${index}`}
              placeholder={t('До')}
              data-testid={`spec-to-${index}`}
            />
          </InlineInputs>
          <IconButton
            variant="ghost"
            onClick={() => handleDeleteSpec(index)}
            data-testid={`spec-delete-${index}`}
          >
            <MdDeleteForever />
          </IconButton>
        </li>
      ))}
    </ExtendableBlock>
  );
};
